import React, { useState } from 'react'
import { useForm, usePage } from '@inertiajs/react'
import {
  Alert, Box, Button, TextField, FormControl, InputLabel, Select, MenuItem, Grid,
  Paper, Typography, IconButton, Divider, Autocomplete, FormHelperText,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'

Form.layout = (page) => <AppShell>{page}</AppShell>

export default function Form({ property, contract, units, contacts, action, errors: serverErrors = {} }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const isEdit = action === 'edit'

  const { data, setData, post, put, processing } = useForm({
    contract_type: contract.contract_type || 'OwnershipContract',
    unit_id: contract.unit_id || '',
    number: contract.number || '',
    start_date: contract.start_date || '',
    end_date: contract.end_date || '',
    notice_period: contract.notice_period || '',
    contact_ids: contract.contact_ids || [],
    new_contacts: [],
  })

  const [newContacts, setNewContacts] = useState([])
  const [showInlineForm, setShowInlineForm] = useState(false)
  const [inlineContact, setInlineContact] = useState({ salutation: 'herr', name1: '', name2: '', title: '' })

  const allErrors = { ...serverErrors }
  const basePath = `/m/${tenant.slug}/properties/${property.id}/contracts`

  const handleSubmit = (e) => {
    e.preventDefault()
    const payload = {
      contract: {
        contract_type: data.contract_type,
        unit_id: data.unit_id,
        number: data.number,
        start_date: data.start_date,
        end_date: data.end_date,
        notice_period: data.notice_period,
        contact_ids: data.contact_ids,
        new_contacts: newContacts.reduce((acc, c, i) => {
          acc[i] = c
          return acc
        }, {}),
      },
    }

    if (isEdit) {
      put(`${basePath}/${contract.id}`, { data: payload })
    } else {
      post(basePath, { data: payload })
    }
  }

  const addExistingContact = (contactId) => {
    if (contactId && !data.contact_ids.includes(contactId)) {
      setData('contact_ids', [...data.contact_ids, contactId])
    }
  }

  const removeExistingContact = (contactId) => {
    setData('contact_ids', data.contact_ids.filter((id) => id !== contactId))
  }

  const addInlineContact = () => {
    if (!inlineContact.name1 || !inlineContact.name2) return
    setNewContacts([...newContacts, { ...inlineContact }])
    setInlineContact({ salutation: 'herr', name1: '', name2: '', title: '' })
    setShowInlineForm(false)
  }

  const removeInlineContact = (idx) => {
    setNewContacts(newContacts.filter((_, i) => i !== idx))
  }

  const selectedContacts = data.contact_ids
    .map((id) => contacts.find((c) => c.id === id))
    .filter(Boolean)

  return (
    <Box>
      <PageHeader
        title={isEdit ? 'Vertrag bearbeiten' : 'Neuer Vertrag'}
        breadcrumbs={[
          { label: 'Verträge', href: basePath },
          { label: isEdit ? 'Bearbeiten' : 'Neu' },
        ]}
      />

      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', maxWidth: 600 }}>
        <form onSubmit={handleSubmit}>
          {allErrors.base && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="base-error">
              {allErrors.base[0]}
            </Alert>
          )}
          <Grid container spacing={2}>
            {/* Contract type */}
            {!isEdit && (
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth size="small" error={!!allErrors.type}>
                  <InputLabel>Vertragstyp</InputLabel>
                  <Select
                    value={data.contract_type}
                    onChange={(e) => setData('contract_type', e.target.value)}
                    label="Vertragstyp"
                    data-testid="contract-type-select"
                  >
                    <MenuItem value="OwnershipContract">Eigentum</MenuItem>
                    <MenuItem value="LeaseContract">Miete</MenuItem>
                    <MenuItem value="Vacancy">Leerstand</MenuItem>
                  </Select>
                  {allErrors.type && <FormHelperText>{allErrors.type[0]}</FormHelperText>}
                </FormControl>
              </Grid>
            )}

            {/* Unit */}
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth size="small" error={!!allErrors.unit_id || !!allErrors.unit}>
                <InputLabel>Einheit</InputLabel>
                <Select
                  value={data.unit_id}
                  onChange={(e) => setData('unit_id', e.target.value)}
                  label="Einheit"
                  data-testid="unit-select"
                >
                  {units.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.number}{u.location ? ` – ${u.location}` : ''}
                    </MenuItem>
                  ))}
                </Select>
                {(allErrors.unit_id || allErrors.unit) && (
                  <FormHelperText>{(allErrors.unit_id || allErrors.unit)[0]}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Contract number */}
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Vertragsnummer"
                value={data.number}
                onChange={(e) => setData('number', e.target.value)}
                fullWidth
                size="small"
                error={!!allErrors.number}
                helperText={allErrors.number?.[0]}
                slotProps={{ htmlInput: { 'data-testid': 'number-input' } }}
              />
            </Grid>

            {/* Start date */}
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Beginn"
                type="date"
                value={data.start_date}
                onChange={(e) => setData('start_date', e.target.value)}
                fullWidth
                size="small"
                required
                error={!!allErrors.start_date}
                helperText={allErrors.start_date?.[0]}
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { 'data-testid': 'start-date-input' },
                }}
              />
            </Grid>

            {/* End date */}
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Ende"
                type="date"
                value={data.end_date}
                onChange={(e) => setData('end_date', e.target.value)}
                fullWidth
                size="small"
                error={!!allErrors.end_date}
                helperText={allErrors.end_date?.[0]}
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { 'data-testid': 'end-date-input' },
                }}
              />
            </Grid>

            {/* Notice period */}
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Kündigungsfrist"
                value={data.notice_period}
                onChange={(e) => setData('notice_period', e.target.value)}
                fullWidth
                size="small"
                slotProps={{ htmlInput: { 'data-testid': 'notice-period-input' } }}
              />
            </Grid>
          </Grid>

          {/* Contact assignment section */}
          {data.contract_type !== 'Vacancy' && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                Kontakte zuordnen
              </Typography>

              {/* Existing contacts selector */}
              <Autocomplete
                options={contacts.filter((c) => !data.contact_ids.includes(c.id))}
                getOptionLabel={(option) => `${option.name} (${option.type === 'Person' ? 'Person' : 'Unternehmen'})`}
                onChange={(_, value) => {
                  if (value) addExistingContact(value.id)
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Bestehenden Kontakt hinzufügen"
                    size="small"
                    fullWidth
                    data-testid="contact-search"
                  />
                )}
                value={null}
                sx={{ mb: 2 }}
              />

              {/* Selected existing contacts */}
              {selectedContacts.map((c, idx) => (
                <Box
                  key={c.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: 1, backgroundColor: 'background.subtle', borderRadius: 1 }}
                  data-testid={`selected-contact-${c.id}`}
                >
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {idx + 1}. {c.name} ({c.type === 'Person' ? 'Person' : 'Unternehmen'})
                  </Typography>
                  <IconButton size="small" onClick={() => removeExistingContact(c.id)} data-testid={`remove-contact-${c.id}`}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}

              {/* Inline new contacts */}
              {newContacts.map((nc, idx) => (
                <Box
                  key={idx}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: 1, backgroundColor: 'info.lighter', borderRadius: 1 }}
                  data-testid={`new-contact-${idx}`}
                >
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {selectedContacts.length + idx + 1}. {nc.name2} {nc.name1} (Neu)
                  </Typography>
                  <IconButton size="small" onClick={() => removeInlineContact(idx)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}

              {/* Inline contact creation form */}
              {showInlineForm ? (
                <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>Neuen Kontakt anlegen</Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Anrede</InputLabel>
                        <Select
                          value={inlineContact.salutation}
                          onChange={(e) => setInlineContact({ ...inlineContact, salutation: e.target.value })}
                          label="Anrede"
                          data-testid="inline-salutation"
                        >
                          <MenuItem value="herr">Herr</MenuItem>
                          <MenuItem value="frau">Frau</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Titel</InputLabel>
                        <Select
                          value={inlineContact.title}
                          onChange={(e) => setInlineContact({ ...inlineContact, title: e.target.value })}
                          label="Titel"
                          data-testid="inline-title"
                        >
                          <MenuItem value="">—</MenuItem>
                          <MenuItem value="dr">Dr.</MenuItem>
                          <MenuItem value="prof">Prof.</MenuItem>
                          <MenuItem value="prof_dr">Prof. Dr.</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        label="Vorname"
                        value={inlineContact.name2}
                        onChange={(e) => setInlineContact({ ...inlineContact, name2: e.target.value })}
                        fullWidth
                        size="small"
                        required
                        slotProps={{ htmlInput: { 'data-testid': 'inline-name2' } }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        label="Nachname"
                        value={inlineContact.name1}
                        onChange={(e) => setInlineContact({ ...inlineContact, name1: e.target.value })}
                        fullWidth
                        size="small"
                        required
                        slotProps={{ htmlInput: { 'data-testid': 'inline-name1' } }}
                      />
                    </Grid>
                  </Grid>
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={addInlineContact}
                      data-testid="confirm-inline-contact"
                    >
                      Hinzufügen
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setShowInlineForm(false)}
                    >
                      Abbrechen
                    </Button>
                  </Box>
                </Paper>
              ) : (
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setShowInlineForm(true)}
                  sx={{ mt: 1 }}
                  data-testid="add-new-contact-btn"
                >
                  Neuen Kontakt anlegen
                </Button>
              )}
            </>
          )}

          {/* Submit */}
          <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={processing}
              data-testid="submit-btn"
            >
              {processing ? 'Speichern...' : (isEdit ? 'Speichern' : 'Vertrag anlegen')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => router.visit(basePath)}
              data-testid="cancel-btn"
            >
              Abbrechen
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  )
}
